import { View, Text, ActivityIndicator, ScrollView, TouchableOpacity } from 'react-native'
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import ScreenWrapper from '@app/hoc/ScreenWrapper'
import CommonHeader from '@app/sharedComponents/Header/Header'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import { PrivateStackParamList } from '@app/navigationApp/types'
import { ThemedText, ThemedTouchableOpacity } from '@app/theme/Components'
import { Theme, ThemedView } from '@app/theme'
import Stepper from '@app/sharedComponents/Stepper/Stepper'
import { getStepper } from '../VerifyBankAccount/services/verifyBankService'
import { useAppDispatch, useAppSelector } from '@app/store/hooks'
import Dropdown from '@app/sharedComponents/Dropdown/Dropdown'
import {
    getPresignedUrlForAdditionalDocumentToUploadAction,
    getSupportingDocumentsByLender,
    onSubmitForApproval
} from './supportingDocumentService'
import { DOCUMENT_TYPE, transformDocumentDetails, UploadDocument } from './util'
import Button from '@app/sharedComponents/Button/Button'
import ShieldCheckIcon from '@app/assets/svg/ShieldCheckIcon'
import UploadDocumentCard from './components/UploadDocumentCard'
import { CaretLeft, ArrowLeft } from '@app/sharedComponents/Icons';
import { useStepperData } from '@app/hooks/useStepperData'
import { useTheme } from '@shopify/restyle'
import { showErrorSheet } from '@app/store/slices/errorSlice'
import { trackEvent } from '@app/utils/amplitude'

type ScreenProps = NativeStackScreenProps<PrivateStackParamList, 'SUPPORTING_DOCUMENT'>;

const SupportingDocuments = ({ navigation, route }: ScreenProps) => {

    const applicationId = useAppSelector(state => state.auth?.userDetails?.linkedApplications[0]?.applicationId);
    const currentStepId = useAppSelector(state => state.auth?.userDetails?.linkedApplications[0]?.currentStepId);

    const { colors } = useTheme<Theme>()
    const scrollRef = useRef<ScrollView>(null)
    const dispatch = useAppDispatch();


    const { stepperData, isLoading: stepperIsLoading } = useStepperData()

    const [isLoading, setIsLoading] = useState(false);
    const [selectedOption, setSelectedOption] = useState<{ label: string, value: string }>({ label: '', value: '' });
    const [headerLabel, setHeaderLabel] = useState('Link bank account');
    const [dropdownLabel, setDropdownLabel] = useState('Select document type');

    const [title, setTitle] = useState('Provide additional documents to verify your bank account')
    const [dropDownPlaceholder, setDropDownPlaceHolder] = useState('Select document type')

    const [selectedFrontFile, setSelectedFrontFile] = useState<any>(null);
    const [selectedBackFile, setSelectedBackFile] = useState<any>(null);

    const [presignedUrl, setPresignedUrl] = useState<{
        imageNameFrontImage: string,
        preSignedUrlForFrontImage: string,
        imageNameBackImage?: string,
        preSignedUrlForBackImage?: string
    } | null>(null);
    const [dropdownData, setDropdownData] = useState<UploadDocument[]>([]);

    const [isAppealLoading, setIsAppealLoading] = useState(false);


    const frontFileExtension = selectedFrontFile && selectedFrontFile?.name?.split('.').pop();
    const backFileExtension = selectedBackFile && selectedBackFile?.name?.split('.').pop();

    const isForBankAccountVerification = currentStepId === 'BANK_ACCOUNT_VERIFICATION'


    const getSupportingDocumentsData = async () => {
        try {
            const response = await getSupportingDocumentsByLender({ applicationId: applicationId });
            const transformedData = transformDocumentDetails(response?.supportingDocumentAllowed?.documentDetails);
            setDropdownData(transformedData);
        } catch (error) {
            console.error(error);
        } finally {
        }
    }

    const getPresignedUrlForAdditionalDocumentToUpload = async () => {
        try {
            const response = await getPresignedUrlForAdditionalDocumentToUploadAction({
                applicationId: applicationId,
                documentType: selectedOption?.value,
                currentStepId: currentStepId
            });
            setPresignedUrl(response);
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        if (selectedOption?.label !== "") {
            getPresignedUrlForAdditionalDocumentToUpload();
        }
    }, [selectedOption?.label])

    useEffect(() => {
        setIsLoading(true);
        Promise.all([getSupportingDocumentsData()]).then(() => {
            setIsLoading(false);
        });
    }, []);


    useLayoutEffect(() => {
        // update header label based on current step id
        if (currentStepId === 'KYC_DOCUMENTS') {
            setHeaderLabel('Supporting documents');
            setDropdownLabel('Select document type');
        }
        if (currentStepId === 'BANK_ACCOUNT_VERIFICATION') {
            setHeaderLabel('Link bank account');
            setDropdownLabel('Select document type');
        }
        if (currentStepId === 'KYC_PHOTO_VERIFICATION') {
            setHeaderLabel('Photo verification');
            setDropdownLabel('Select ID proof for upload');
            setTitle("Provide additional document for photo verification")
            setDropDownPlaceHolder('Select ID proof for upload')
        }
        if (currentStepId === 'DIGIO_MANDATE_SIGN') {
            setHeaderLabel('Link bank account');
            setDropdownLabel('Select document type');
        }
    }, [currentStepId]);

    const isBackSideRequired = dropdownData?.find(doc => doc.type === selectedOption?.value)?.isBackSideRequired;

    const isButtonDisabled = !selectedFrontFile || isAppealLoading || (isBackSideRequired && !selectedBackFile);

    const handleSubmitForApproval = async () => {
        trackEvent('SUBMITBANKDOCS_SUBMIT_CLICKED',{selectedDoc: selectedOption?.value})
        setIsAppealLoading(true);
        console.log('selectedOption', presignedUrl);
        try {
            await onSubmitForApproval({
                applicationId: applicationId,
                currentStepId: currentStepId,
                frontFile: selectedFrontFile,
                backFile: selectedBackFile,
                documentType: selectedOption?.value,
                frontPresignedUrl: presignedUrl?.preSignedUrlForFrontImage!,
                backPresignedUrl: isBackSideRequired ? presignedUrl?.preSignedUrlForBackImage! : null,
                frontFileExtension: frontFileExtension,
                backFileExtension: backFileExtension,
                imageNameFrontImage: presignedUrl?.imageNameFrontImage,
                imageNameBackImage: isBackSideRequired ? presignedUrl?.imageNameBackImage : null
            });
            navigation.reset({
                index: 0,
                routes: [{ name: 'BLOCKED' }]
            })
        } catch (err: any) {
            dispatch(showErrorSheet({
                isErrorSheetVisible: true,
                errorMessage: err?.data?.message,
                closeOnBackdropPress: true,
                errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                errorHeading: err?.data?.header,
                showErrorHeading: true,
                btnName:'Ok, got it!',
            }))
        } finally {
            setIsAppealLoading(false);
        }
    }


    if (isLoading || stepperIsLoading) {
        return <ThemedView flex={1} bg='bg-surface-primary' justifyContent='center' alignItems='center'>
            <ActivityIndicator size={12} />
        </ThemedView>
    }

    return (
        <ScreenWrapper> 
            <ThemedView flex={1} bg='bg-surface-primary'>
                <CommonHeader
                    navigation={navigation}
                    pageName={route.name}
                    showNeedHelp={true}
                    hideBottomBorder={true}
                    label={headerLabel}
                />
                <Stepper stepperData={stepperData} />
                <ScrollView ref={scrollRef}>
                    {isAppealLoading && <ThemedView zIndex={100} opacity={0.5} position='absolute' top={0} left={0} right={0} bottom={0} flex={1} bg='bg-surface-primary' justifyContent='center' alignItems='center'/>}
                    <ThemedView padding='lg' paddingTop='2xl' bg='bg-surface-primary'>
                        <ThemedTouchableOpacity mb='2xl' flexDirection='row' alignItems='center'
                            onPress={navigation.goBack}>
                            <CaretLeft size={16} color={colors['text-primary']} />
                            <ThemedText ml='xs' variant='b3s' color='text-primary'>Go back
                            </ThemedText>
                        </ThemedTouchableOpacity>

                        <ThemedText mb='2xl' variant='h5s' color='text-primary'>
                            {title}
                        </ThemedText>
                        {!isForBankAccountVerification && (
                        <ThemedView borderRadius={"sm"} mb={"2xl"} bg={"bg-action-disabled"} paddingVertical={"sm"}
                            paddingHorizontal={"lg"} alignItems={"center"} justifyContent={"center"}>
                            <ThemedText variant={"b4r"} color={"text-primary"}>
                                We'll match the captured photo with the photo on your document. Upload a document that
                                displays a clear and recent photo.
                            </ThemedText>
                        </ThemedView>
                        )}

                        {currentStepId === 'BANK_ACCOUNT_VERIFICATION' && <ThemedText mb='sm' variant='b4r' color='text-secondary'>For bank account: XXXX {route?.params?.accountNumber?.slice(-4)}</ThemedText>}

                        {!isForBankAccountVerification && (
                        <Dropdown scrollRef={scrollRef} activeLabel={dropdownLabel} label={dropdownLabel} options={dropdownData?.map((item: UploadDocument) => ({ label: item.label, value: item.type })) || []} selectedOption={selectedOption} onSelect={setSelectedOption} />
                        )}

                        {currentStepId === "BANK_ACCOUNT_VERIFICATION" &&
                            <>
                                <Dropdown scrollRef={scrollRef} activeLabel='Document type' label={dropDownPlaceholder}
                                    options={dropdownData?.map((item: UploadDocument) => ({
                                        label: item.label,
                                        value: item.type
                                    })) || []} selectedOption={selectedOption} onSelect={setSelectedOption} />
                            </>}
                        {selectedOption?.value === DOCUMENT_TYPE.PASSBOOK && (
                            <UploadDocumentCard selectedFrontFile={selectedFrontFile}
                                setSelectedFrontFile={setSelectedFrontFile}
                                selectedBackFile={selectedBackFile}
                                isUploading={isAppealLoading}
                                setSelectedBackFile={setSelectedBackFile} />
                        )}
                        {selectedOption?.value === DOCUMENT_TYPE.CANCELLED_CHEQUE && (
                            <UploadDocumentCard selectedFrontFile={selectedFrontFile}
                                setSelectedFrontFile={setSelectedFrontFile}
                                selectedBackFile={selectedBackFile}
                                isUploading={isAppealLoading}
                                setSelectedBackFile={setSelectedBackFile} />
                        )}
                        {selectedOption?.value === DOCUMENT_TYPE.BANK_STATEMENT && (
                            <UploadDocumentCard selectedFrontFile={selectedFrontFile}
                                setSelectedFrontFile={setSelectedFrontFile}
                                selectedBackFile={selectedBackFile}
                                isUploading={isAppealLoading}
                                setSelectedBackFile={setSelectedBackFile} />
                        )}
                        {selectedOption?.value === DOCUMENT_TYPE.DRIVING_LICENSE && (
                            <UploadDocumentCard selectedFrontFile={selectedFrontFile}
                                isBackSide={dropdownData?.find(doc => doc.type === DOCUMENT_TYPE.DRIVING_LICENSE)?.isBackSideRequired}
                                setSelectedFrontFile={setSelectedFrontFile}
                                selectedBackFile={selectedBackFile}
                                isUploading={isAppealLoading}
                                setSelectedBackFile={setSelectedBackFile} />
                        )}
                        {selectedOption?.value === DOCUMENT_TYPE.OTHER_DOCUMENT && (
                            <UploadDocumentCard selectedFrontFile={selectedFrontFile}
                                setSelectedFrontFile={setSelectedFrontFile}
                                selectedBackFile={selectedBackFile}
                                //@ts-ignore
                                isBackSide={dropdownData?.find(doc => doc.type === DOCUMENT_TYPE.OTHER_DOCUMENT)?.isBackSideRequired}
                                isUploading={isAppealLoading}
                                setSelectedBackFile={setSelectedBackFile} />
                        )}
                        {selectedOption?.value === DOCUMENT_TYPE.ELECTION_CARD && (
                            <UploadDocumentCard selectedFrontFile={selectedFrontFile}
                                setSelectedFrontFile={setSelectedFrontFile}
                                selectedBackFile={selectedBackFile}
                                isBackSide={dropdownData?.find(doc => doc.type === DOCUMENT_TYPE.ELECTION_CARD)?.isBackSideRequired}
                                //@ts-ignore
                                isUploading={isAppealLoading}
                                setSelectedBackFile={setSelectedBackFile} />
                        )}
                        {selectedOption?.value === DOCUMENT_TYPE.PASSPORT && (
                            <UploadDocumentCard selectedFrontFile={selectedFrontFile}
                                setSelectedFrontFile={setSelectedFrontFile}
                                selectedBackFile={selectedBackFile}
                                isUploading={isAppealLoading}
                                //@ts-ignore
                                isBackSide={dropdownData?.find(doc => doc.type === DOCUMENT_TYPE.PASSPORT)?.isBackSideRequired}
                                setSelectedBackFile={setSelectedBackFile} />
                        )}
                        {selectedOption?.value === DOCUMENT_TYPE.VOTER_ID && (
                            <UploadDocumentCard selectedFrontFile={selectedFrontFile}
                                setSelectedFrontFile={setSelectedFrontFile}
                                selectedBackFile={selectedBackFile}
                                isUploading={isAppealLoading}
                                //@ts-ignore
                                isBackSide={dropdownData?.find(doc => doc.type === DOCUMENT_TYPE.VOTER_ID)?.isBackSideRequired}
                                setSelectedBackFile={setSelectedBackFile} />
                        )}
                    </ThemedView>
                </ScrollView>
                <ThemedView borderTopWidth={1} borderColor='border-secondary' padding='lg' bg='bg-surface-primary'>
                    <ThemedView mb='lg' flexDirection='row' justifyContent='center' alignItems='center'>
                        <ShieldCheckIcon size={14} />
                        <ThemedText textAlign='center' ml='sm' color='text-emphasis-secondary' variant='b4r'>
                            Your data is secure with us
                        </ThemedText>
                    </ThemedView>
                    <Button
                        eventName='SUPPORTING_DOCUMENTS_SUBMIT_FOR_APPROVAL'
                        disabled={isButtonDisabled}
                        size='MD'
                        handlePress={handleSubmitForApproval}
                        bg='bg-action'
                        textColor='text-white'
                        label='Submit for approval'
                        buttonLoader={isAppealLoading}
                    />

                </ThemedView>
            </ThemedView>
        </ScreenWrapper>
    )
}

export default SupportingDocuments